import { useState, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router';
import {
  AppShell,
  Box,
  Burger,
  Divider,
  Group,
  NavLink,
  Stack,
  Text,
  ActionIcon,
  rem,
} from '@mantine/core';
import { IconLogout, IconSettings } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { SidebarHelpCta } from './SidebarHelpCta';
import { VdzLogo } from '../VdzLogo';
import classes from './AppLayout.module.css';
import { buildNavSections, hasReadableTransferArea, type NavItem } from './navSections';

export function AppLayout() {
  const [opened, setOpened] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = user?.roles?.includes('ROLE_ADMIN') ?? false;
  const isTrustee = user?.roles?.includes('ROLE_TRUSTEE') ?? false;
  // D-02: eigene bedingte Sektion statt Eintrag in adminNav -- adminNav.push haengt nur an
  // isAdmin und waere sonst fuer jeden Admin sichtbar, auch ohne freigeschaltete Kategorie.
  const hasAccidentCategory = user?.enabledReportCategories?.includes('accident') ?? false;
  // D-23: Navigationseintrag "Übertragungen" fuer jede Person mit mindestens einem lesbaren
  // Bereich -- die Bereichsregel selbst sitzt in `hasReadableTransferArea` (navSections.ts).
  const canReadTransfers = hasReadableTransferArea({
    isAdmin,
    isTrustee,
    enabledReportCategories: user?.enabledReportCategories ?? [],
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderNavItem = (item: NavItem) => {
    const isActive =
      location.pathname === item.path ||
      (item.matchPrefix === true && location.pathname.startsWith(item.path + '/'));
    return (
      <NavLink
        key={item.path}
        label={item.label}
        leftSection={<item.icon size={18} stroke={1.5} color="var(--mantine-color-vdzGray-8)" />}
        rightSection={
          item.badge !== undefined ? (
            <Box
              style={{
                background: 'var(--mantine-color-vdzYellow-3)',
                color: 'var(--mantine-color-vdzGray-9)',
                fontSize: rem(10),
                fontWeight: 700,
                padding: '1px 8px',
                borderRadius: rem(10),
              }}
            >
              {item.badge}
            </Box>
          ) : null
        }
        active={isActive}
        onClick={() => {
          navigate(item.path);
          setOpened(false);
        }}
        mb={2}
        variant="filled"
        color="vdzGray.1"
        styles={{
          root: {
            borderRadius: rem(6),
            padding: '1rem',
          },
          label: {
            fontSize: rem(13),
            fontWeight: 500,
            color: 'var(--mantine-color-vdzGray-8)',
          },
        }}
      />
    );
  };

  const navSections = useMemo(
    () =>
      buildNavSections({
        isAdmin,
        isTrustee,
        hasAccidentCategory,
        hasReadableTransferArea: canReadTransfers,
      }),
    [isAdmin, isTrustee, hasAccidentCategory, canReadTransfers]
  );

  return (
    <AppShell
      navbar={{
        width: 240,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="lg"
    >
      <AppShell.Navbar p="md" pt="xl" bg="white">
        <Stack gap={0} h="100%">
          <Group justify="center" align="center" wrap="nowrap">
            <Group gap="xs" align="end" wrap="nowrap">
              <VdzLogo width={48} color="var(--mantine-color-vdzGray-8)" />
              <Text size="sm" lh={1}>
                Meldeplattform
              </Text>
            </Group>
            <Burger
              opened={opened}
              onClick={() => setOpened((o) => !o)}
              hiddenFrom="sm"
              size="sm"
            />
          </Group>
          <Divider my="xl" color="vdzGray.2" />

          {navSections.map((section, idx) => (
            <Box key={idx}>
              {idx > 0 && <Divider my="md" color="vdzGray.2" />}
              {section.map(renderNavItem)}
            </Box>
          ))}

          <Box style={{ flex: 1 }} />


          <Stack gap="xs">
            <NavLink
              label="Logout"
              leftSection={<IconLogout size={18} stroke={1.5} />}
              onClick={handleLogout}
              styles={{
                root: { borderRadius: rem(6), padding: `${rem(8)} ${rem(12)}` },
                label: { fontSize: rem(13), color: 'var(--mantine-color-vdzGray-8)' },
              }}
            />

            {user && (
              <Group justify="space-between" px="xs" py="xs" wrap="nowrap">
                <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                  <Text size="sm" fw={600} truncate>
                    {user.firstName} {user.lastName}
                  </Text>
                  <Text size="xs" c="vdzGray.7" truncate>
                    {user.email}
                  </Text>
                </Stack>
                <ActionIcon variant="subtle" color="vdzGray.7" size="sm" aria-label="Einstellungen">
                  <IconSettings size={16} stroke={1.5} />
                </ActionIcon>
              </Group>
            )}

            <SidebarHelpCta />

            <Text size="xs" c="vdzGray.5" mt="xs">
              Meldeportal v1.0
            </Text>
          </Stack>
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main className={classes.shellMain} pt="xl">
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
